import { useState, useCallback } from 'react';
import {
  validateRequired, validateMinLength, validatePositiveNumber,
  validateDateNotFuture, validateDateAfter,
  validatePercentage, validateOptionalPositiveNumber,
} from '../../utils/validation';

const FIXED_INCOME_TYPES = ['government_bond', 'corporate_bond', 'treasury_bill', 'fixed_deposit'];
const BOND_TYPES = ['government_bond', 'corporate_bond'];
const FACE_VALUE_TYPES = ['government_bond', 'corporate_bond', 'treasury_bill'];

export function useInvestmentValidation(formData) {
  // ``touched`` is now populated only when the user *edits* a field (or on
  // submit), NOT on blur. Merely tabbing through a still-empty field will not
  // trigger a "required" error. This matches Stripe / Notion / Linear UX.
  const [touched, setTouched] = useState({});

  const touch = useCallback((field) => {
    setTouched(t => ({ ...t, [field]: true }));
  }, []);

  const touchAll = useCallback(() => {
    setTouched({
      name: true, type: true, amount: true, currency: true,
      purchase_date: true, interest_rate: true,
      maturity_date: true, share_price: true,
      face_value: true, coupon_frequency: true,
    });
  }, []);

  const getErrors = useCallback(() => {
    const e = {};
    const invType = formData.type;
    const isFixedIncome = FIXED_INCOME_TYPES.includes(invType);
    const needsFaceValue = FACE_VALUE_TYPES.includes(invType);
    const needsFrequency = BOND_TYPES.includes(invType);

    // Always required
    e.name = validateRequired(formData.name, 'Investment name') || validateMinLength(formData.name, 2, 'Investment name');
    e.type = validateRequired(formData.type, 'Investment type');
    e.amount = validatePositiveNumber(formData.amount, 'Amount invested');
    e.currency = validateRequired(formData.currency, 'Currency');

    // Optional fields — only validate if provided
    e.purchase_date = validateDateNotFuture(formData.purchase_date, 'Purchase date');
    e.share_price = validateOptionalPositiveNumber(formData.share_price, 'Share price');

    // Fixed-income required fields
    if (isFixedIncome) {
      e.interest_rate = formData.interest_rate
        ? validatePercentage(formData.interest_rate, 'Interest rate')
        : 'Interest rate is required';
      e.maturity_date = formData.maturity_date
        ? validateDateAfter(formData.maturity_date, formData.purchase_date, 'Maturity date', 'purchase date')
        : 'Maturity date is required';
    } else {
      e.interest_rate = validatePercentage(formData.interest_rate, 'Interest rate');
      e.maturity_date = validateDateAfter(formData.maturity_date, formData.purchase_date, 'Maturity date', 'purchase date');
    }

    if (needsFaceValue) {
      e.face_value = formData.face_value
        ? validatePositiveNumber(formData.face_value, 'Face value')
        : 'Face value is required';
    } else {
      e.face_value = '';
    }

    if (needsFrequency) {
      e.coupon_frequency = formData.coupon_frequency ? '' : 'Frequency is required';
    } else {
      e.coupon_frequency = '';
    }

    return e;
  }, [formData]);

  const errors = getErrors();
  const visibleErrors = {};
  for (const key in errors) {
    visibleErrors[key] = touched[key] ? errors[key] : '';
  }

  const hasErrors = Object.values(errors).some(e => e);

  return { errors: visibleErrors, touch, touchAll, hasErrors };
}
